import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AtSign, Loader2, Lock, LogOut, Mail, Palette, Plus, RefreshCw, Save, Trash2, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { id: "en_etude", label: "En étude", chip: "border-accent/30 bg-orange-50 text-accent" },
  { id: "chiffre", label: "Chiffré", chip: "border-blue-200 bg-blue-50 text-blue-700" },
  { id: "valide", label: "Validé", chip: "border-green-200 bg-green-50 text-green-700" },
];
const statusLabel = (id) => (STATUSES.find((s) => s.id === id) || STATUSES[0]).label;
const statusChip = (id) => (STATUSES.find((s) => s.id === id) || STATUSES[0]).chip;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full rounded-xl border border-black/15 bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition-colors focus:border-ink";

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("trd_admin_token"));
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);
  const [loading, setLoading] = useState(() => !!sessionStorage.getItem("trd_admin_token"));
  const [cfg, setCfg] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("palette");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [{ data: c }, { data: q }, { data: s }] = await Promise.all([
          axios.get(`${API}/config`),
          axios.get(`${API}/admin/quotes`, { headers }),
          axios.get(`${API}/admin/settings`, { headers }),
        ]);
        setCfg({ materials: c.materials, colors: c.colors });
        setQuotes(q);
        setOwnerEmail(s.owner_email || "");
      } catch (e) {
        if (e?.response?.status === 401) {
          sessionStorage.removeItem("trd_admin_token");
          setToken(null);
          toast.error("Session expirée — reconnectez-vous.");
        } else {
          toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("trd_admin_token", data.token);
      setToken(data.token);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLogging(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("trd_admin_token");
    setToken(null);
    setCfg(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, { materials: cfg.materials, colors: cfg.colors }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Catalogue enregistré — le site public est déjà à jour.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await axios.put(`${API}/admin/settings`, { owner_email: ownerEmail.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Adresse de réception mise à jour — les prochaines demandes y arrivent.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const refreshQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(data);
      toast.success(`${data.length} demande(s) chargée(s).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const updateStatus = async (ref, status) => {
    try {
      await axios.put(`${API}/admin/quotes/${encodeURIComponent(ref)}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((qs) => qs.map((q) => (q.id === ref ? { ...q, status } : q)));
      toast.success(`${ref} — statut « ${statusLabel(status)} » enregistré.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  if (!token) {
    return (
      <div className="dot-grid flex min-h-screen items-center justify-center px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="corner-card relative w-full max-w-sm rounded-[20px] border border-black/10 bg-white p-8"
        >
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-ink">
            <Lock className="h-5 w-5 text-white" />
          </div>
          <h1 className="mt-5 text-center font-display text-xl font-extrabold tracking-tight text-ink">Espace atelier</h1>
          <p className="mt-1 text-center text-sm text-neutral-600">Réservé à l'atelier TroiDéo.</p>
          <input
            data-testid="admin-login-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe administrateur"
            className={`${inputCls} mt-6`}
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={logging}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent disabled:opacity-60"
          >
            {logging && <Loader2 className="h-4 w-4 animate-spin" />} Se connecter
          </button>
        </form>
      </div>
    );
  }

  if (loading || !cfg) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  const TABS = [
    { id: "palette", label: "Couleurs & filaments", icon: Palette },
    { id: "demandes", label: `Demandes (${quotes.length})`, icon: Wrench },
    { id: "reglages", label: "Réglages", icon: AtSign },
  ];

  return (
    <div className="min-h-screen bg-mist pb-24">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <span className="font-display text-lg font-extrabold tracking-tight text-ink">Espace atelier</span>
            <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">TroiDéo admin</span>
          </div>
          <div className="flex items-center gap-2">
            {tab !== "reglages" && (
              <button
                data-testid="admin-save-config-button"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
              </button>
            )}
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2.5 text-sm font-medium text-neutral-600 transition-colors hover:border-ink hover:text-ink"
            >
              <LogOut className="h-4 w-4" /> Quitter
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-6 pb-3">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                tab === id ? "bg-ink text-white" : "text-neutral-600 hover:bg-black/5"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 pt-8">
        {tab === "palette" && (
          <section data-testid="admin-panel-palette" className="space-y-6">
            <div className="rounded-[20px] border border-black/10 bg-white p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-base font-bold text-ink">Couleurs standard (filament en stock)</h2>
                <button
                  data-testid="admin-add-color-button"
                  onClick={() => setCfg({ ...cfg, colors: [...cfg.colors, { id: `c${Date.now().toString(36)}`, name: "Nouvelle couleur", hex: "#888888" }] })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3.5 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:border-ink hover:text-ink"
                >
                  <Plus className="h-3.5 w-3.5" /> Ajouter
                </button>
              </div>
              <p className="mt-1 text-xs text-neutral-500">Ces couleurs sont proposées telles quelles aux clients. En dehors de cette liste, le client choisit « Autre couleur » (délai +1 semaine).</p>
              <div className="mt-4 space-y-2">
                {cfg.colors.map((c, i) => (
                  <div key={c.id + i} data-testid={`admin-color-row-${i}`} className="flex items-center gap-3 rounded-xl border border-black/10 p-3">
                    <input type="color" value={c.hex} onChange={(e) => { const colors = [...cfg.colors]; colors[i] = { ...c, hex: e.target.value }; setCfg({ ...cfg, colors }); }} className="h-9 w-9 cursor-pointer rounded-lg border border-black/10 bg-white p-0.5" />
                    <input value={c.name} onChange={(e) => { const colors = [...cfg.colors]; colors[i] = { ...c, name: e.target.value }; setCfg({ ...cfg, colors }); }} className={inputCls} placeholder="Nom affiché au client" />
                    <button
                      data-testid={`admin-color-delete-${i}`}
                      onClick={() => setCfg({ ...cfg, colors: cfg.colors.filter((_, j) => j !== i) })}
                      className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      aria-label={`Supprimer la couleur ${c.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[20px] border border-black/10 bg-white p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-base font-bold text-ink">Types de filament</h2>
                <button
                  data-testid="admin-add-material-button"
                  onClick={() => setCfg({ ...cfg, materials: [...cfg.materials, { id: `m${Date.now().toString(36)}`, name: "Nouveau filament" }] })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3.5 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:border-ink hover:text-ink"
                >
                  <Plus className="h-3.5 w-3.5" /> Ajouter
                </button>
              </div>
              <p className="mt-1 text-xs text-neutral-500">Les matières proposées aux clients pour l'impression de leur pièce.</p>
              <div className="mt-4 space-y-2">
                {cfg.materials.map((m, i) => (
                  <div key={m.id + i} data-testid={`admin-material-row-${i}`} className="flex items-center gap-3 rounded-xl border border-black/10 p-3">
                    <input value={m.name} onChange={(e) => { const materials = [...cfg.materials]; materials[i] = { ...m, name: e.target.value }; setCfg({ ...cfg, materials }); }} className={inputCls} placeholder="Nom de la matière" />
                    <button
                      data-testid={`admin-material-delete-${i}`}
                      onClick={() => setCfg({ ...cfg, materials: cfg.materials.filter((_, j) => j !== i) })}
                      className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      aria-label={`Supprimer la matière ${m.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {tab === "demandes" && (
          <section data-testid="admin-panel-demandes" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-600">Les demandes envoyées via le site, les plus récentes d'abord. Changez le statut pour suivre chaque dossier.</p>
              <button
                data-testid="admin-refresh-quotes-button"
                onClick={refreshQuotes}
                className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-4 py-2 text-xs font-medium text-neutral-600 transition-colors hover:border-ink hover:text-ink"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Actualiser
              </button>
            </div>
            {quotes.length === 0 && (
              <div className="rounded-[20px] border border-dashed border-black/15 bg-white p-10 text-center text-sm text-neutral-500">
                Aucune demande reçue pour le moment.
              </div>
            )}
            {quotes.map((q, i) => (
              <article key={q.id} data-testid={`admin-quote-card-${i}`} className="corner-card relative rounded-[20px] border border-black/10 bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs tracking-[0.14em] text-accent">{q.id}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400">
                    {q.created_at ? new Date(q.created_at).toLocaleString("fr-FR") : ""}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-ink px-3 py-1 text-[11px] font-medium text-white">
                    {q.project_type === "creation" ? "Création" : "Réparation"}
                  </span>
                  <span
                    data-testid={`quote-status-chip-${i}`}
                    className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${statusChip(q.status || "en_etude")}`}
                  >
                    {statusLabel(q.status || "en_etude")}
                  </span>
                  {q.want_print && <span className="rounded-full border border-black/15 px-3 py-1 text-[11px] text-neutral-600">Impression {q.quantity}×</span>}
                  <span className="rounded-full border border-black/15 px-3 py-1 text-[11px] text-neutral-600">
                    {q.has_dimensions ? `${q.length_mm} × ${q.width_mm} × ${q.height_mm} mm` : "Sans cotes — contact"}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-800">{q.description}</p>
                {q.usage && <p className="mt-1 text-xs text-neutral-500">Utilité : {q.usage}</p>}
                {q.remark && <p className="mt-1 text-xs leading-relaxed text-neutral-500">Remarques : {q.remark}</p>}
                <p className="mt-3 text-xs text-neutral-600">
                  <strong className="font-semibold text-ink">{q.name}</strong> — {q.email}
                  {q.phone ? ` — ${q.phone}` : ""}
                </p>
                {q.want_print && (
                  <p className="mt-1 text-xs text-neutral-600">
                    Filament : {cfg.materials.find((m) => m.id === q.material)?.name || q.material} —{" "}
                    {q.color === "custom"
                      ? "Autre couleur (délai +1 semaine)"
                      : cfg.colors.find((c) => c.id === q.color)?.name || q.color}
                  </p>
                )}
                {q.photo_paths?.length > 0 && (
                  <div className="mt-3 flex gap-2">
                    {q.photo_paths.map((p, j) => (
                      <a key={j} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer" className="flex h-16 w-16 items-center justify-center rounded-lg border border-black/10 bg-mist font-mono text-[9px] uppercase text-neutral-500 transition-colors hover:border-accent">
                        {p.endsWith(".pdf") ? "PDF" : "Photo"}
                      </a>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex items-center gap-3 border-t border-black/5 pt-4">
                  <label className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400">Statut du dossier</label>
                  <select
                    data-testid={`quote-status-select-${i}`}
                    value={q.status || "en_etude"}
                    onChange={(e) => updateStatus(q.id, e.target.value)}
                    className="rounded-full border border-accent/70 bg-white px-4 py-1.5 text-xs font-semibold text-ink outline-none transition-colors focus:border-accent"
                  >
                    {STATUSES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </article>
            ))}
          </section>
        )}

        {tab === "reglages" && (
          <section data-testid="admin-panel-reglages" className="space-y-6">
            <div className="rounded-[20px] border border-black/10 bg-white p-6 sm:p-8">
              <h2 className="flex items-center gap-2 font-display text-base font-bold text-ink">
                <Mail className="h-4 w-4 text-accent" /> Adresse de réception des demandes
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-neutral-500">
                Les demandes de devis (avec cotes) arrivent sur cette adresse — changement immédiat
                et valable pour les demandes suivantes. Les demandes de contact standard (sans
                cotes) partent sur l'adresse contact configurée côté atelier.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="admin-owner-email-input"
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="votre.adresse@gmail.com"
                  className={inputCls}
                />
                <button
                  data-testid="admin-save-owner-email-button"
                  onClick={saveEmail}
                  disabled={savingEmail || !ownerEmail.trim()}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent disabled:opacity-60"
                >
                  {savingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Mettre à jour
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
